import React, { useMemo, useEffect, useRef, useState } from 'react';
import { FileText, HelpCircle, CheckCircle, XCircle, AlertCircle, Loader2, BarChart3, Play } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import {
  useGetQAResultExecutiveSummaryQuery,
  useListQAResultsNewQuery,
  useTriggerQAExtractionMutation,
  type QAExecutiveSummary,
  type QAResultNew,
} from '../../redux/api/qaApi';
import { useAppSelector } from '../../redux/hooks';

interface QAExecutiveSummaryTabProps {
  qaResultId?: string;
  documentId?: string;
}

const COLORS = {
  pass: '#22c55e', // green-500
  fail: '#ef4444', // red-500
  ca: '#6b7280', // gray-500
};

const STALE_PENDING_MS = 3.5 * 60 * 60 * 1000; // 3.5 hours

/** A pending/processing result older than 3.5 hours is considered stale (failed). */
function isStalePending(result: QAResultNew): boolean {
  if (result.status !== 'pending' && result.status !== 'processing') return false;
  const age = Date.now() - new Date(result.created_at).getTime();
  return age > STALE_PENDING_MS;
}

const QAExecutiveSummaryTab: React.FC<QAExecutiveSummaryTabProps> = ({ qaResultId }) => {
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const tenantId = currentSite?.tenant_id;
  const siteId = currentSite?.site_id; // Use site_id from PortfolioSite

  // Fetch QA results for the selected site; refetch when site changes so we never show previous site's data
  const { data: qaResultsData, isLoading: isLoadingResults, isFetching: isFetchingResults, refetch: refetchResults } = useListQAResultsNewQuery(
    {
      site_id: siteId || undefined,
      limit: 100,
      offset: 0,
    },
    { skip: !tenantId || !siteId }
  );

  // Refetch list when site changes so executive summary always reflects the currently selected site
  useEffect(() => {
    if (tenantId && siteId) refetchResults();
  }, [siteId, tenantId, refetchResults]);

  // Driven by site only. Use list data only when not fetching (so we never use stale data from the previous site).
  // Reset to null when site changes to prevent showing previous site's data
  const selectedQAResultId = useMemo(() => {
    if (isFetchingResults) return null;
    const items = qaResultsData?.items ?? [];
    return items.length > 0 ? items[0].id : null;
  }, [qaResultsData, isFetchingResults, siteId]); // Include siteId to reset when site changes

  // Fetch executive summary for the selected QA result
  // Skip if siteId changes (isFetchingResults) to prevent showing stale data from previous site
  const { data: executiveSummary, isLoading: isLoadingSummary, error, refetch: refetchExecutiveSummary } = useGetQAResultExecutiveSummaryQuery(
    selectedQAResultId!,
    {
      skip: !selectedQAResultId || !tenantId || isFetchingResults, // Skip when fetching new site data
      // Refetch when selectedQAResultId changes (which happens when site changes)
      refetchOnMountOrArgChange: true,
    }
  );

  // Track previous siteId to detect site changes
  const prevSiteIdRef = useRef<string | undefined>(siteId);

  // Reset executive summary data when site changes
  const [shouldClearSummary, setShouldClearSummary] = useState(false);

  // Track if charts should render (to prevent dimension warnings)
  const [chartsReady, setChartsReady] = useState(false);

  useEffect(() => {
    if (prevSiteIdRef.current !== undefined && prevSiteIdRef.current !== siteId) {
      // Site changed - clear summary data
      setShouldClearSummary(true);
      setChartsReady(false); // Reset charts ready state on site change
    }
    prevSiteIdRef.current = siteId;
  }, [siteId]);

  // Clear the flag once we have new data or no selected result
  useEffect(() => {
    if (shouldClearSummary && (!selectedQAResultId || executiveSummary)) {
      setShouldClearSummary(false);
    }
  }, [shouldClearSummary, selectedQAResultId, executiveSummary]);

  // QA extraction trigger mutation
  const [triggerQAExtraction, { isLoading: isTriggering }] = useTriggerQAExtractionMutation();

  // Check for pending/processing QA results (exclude stale ones)
  const pendingOrProcessingResult = useMemo(() => {
    if (!qaResultsData?.items) return null;
    return qaResultsData.items.find(
      (result: QAResultNew) => (result.status === 'pending' || result.status === 'processing') && !isStalePending(result)
    );
  }, [qaResultsData]);

  // Get the current QA result being viewed (for task_id lookup)
  const currentQAResult = useMemo(() => {
    if (!qaResultsData?.items || !selectedQAResultId) return null;
    return qaResultsData.items.find(
      (result: QAResultNew) => result.id === selectedQAResultId
    ) || null;
  }, [qaResultsData, selectedQAResultId]);

  // Reset executive summary when site changes to prevent showing previous site's data
  useEffect(() => {
    if (siteId && selectedQAResultId) {
      // Refetch executive summary when site changes to ensure fresh data
      refetchExecutiveSummary();
    }
  }, [siteId, refetchExecutiveSummary]); // Only depend on siteId, not selectedQAResultId to avoid loops

  // Refetch executive summary when QA result status changes to completed
  useEffect(() => {
    const execStatus = executiveSummary?.status || 'processing';
    if (currentQAResult?.status === 'completed' && execStatus !== 'completed' && selectedQAResultId) {
      // Status changed to completed, refetch executive summary to get updated data
      refetchExecutiveSummary();
    }
  }, [currentQAResult?.status, executiveSummary?.status, selectedQAResultId, refetchExecutiveSummary]);


  // Check status from executive summary
  // Don't use fallback if we're clearing summary (site changed) or if we don't have a selected result
  // Only use executiveSummary if we have a selected result and aren't clearing
  const status = (shouldClearSummary || !selectedQAResultId || isFetchingResults)
    ? null
    : (executiveSummary?.status || null);
  const isProcessing = status === 'processing' || status === 'pending';
  const isCompleted = status === 'completed';

  // Enable chart rendering after component is mounted and data is ready
  useEffect(() => {
    if (isCompleted && executiveSummary && !shouldClearSummary) {
      // Small delay to ensure container is ready
      const timer = setTimeout(() => {
        setChartsReady(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setChartsReady(false);
    }
  }, [isCompleted, executiveSummary, shouldClearSummary]);

  // Prepare chart data (only when completed)
  const pieData = useMemo(() => {
    if (!executiveSummary || !isCompleted) return [];
    return [
      { name: 'Pass', value: executiveSummary.total_passed, color: COLORS.pass },
      { name: 'Fail', value: executiveSummary.total_failed, color: COLORS.fail },
      { name: "Can't Assess", value: executiveSummary.total_unanswerable, color: COLORS.ca },
    ];
  }, [executiveSummary, isCompleted]);

  const categoryChartData = useMemo(() => {
    if (!executiveSummary || !isCompleted) return [];
    return executiveSummary.category_breakdown.map(cat => ({
      name: cat.category.length > 20 ? cat.category.substring(0, 17) + '...' : cat.category,
      fullName: cat.category,
      pass: cat.pass,
      fail: cat.fail,
      ca: cat.ca,
    }));
  }, [executiveSummary, isCompleted]);

  const topFailureCategories = useMemo(() => {
    if (!executiveSummary || !isCompleted) return [];
    return executiveSummary.category_breakdown
      .filter(cat => cat.fail > 0)
      .slice(0, 5);
  }, [executiveSummary, isCompleted]);

  if (!tenantId || !siteId) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--color-zone-recessed)' }}>
        <div className="text-center eg-text-primary">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 eg-text-secondary" />
          <p className="text-lg">No Site Selected</p>
          <p className="text-sm mt-2">Please select a site from the sidebar to view QA summary</p>
        </div>
      </div>
    );
  }

  const isLoading = isLoadingResults || isLoadingSummary || (isFetchingResults && !selectedQAResultId);
  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--color-zone-recessed)' }}>
        <Loader2 className="w-8 h-8 animate-spin text-engramiq-green" />
        <span className="ml-3 eg-text-primary">Loading QA summary...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--color-zone-recessed)' }}>
        <div className="text-center eg-text-primary">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <p className="text-lg">Error Loading Summary</p>
          <p className="text-sm mt-2 eg-text-secondary">
            {error && 'data' in error ? JSON.stringify(error.data) : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  // Show processing UI when executive summary exists but status is not completed
  // Only show if executiveSummary matches current selectedQAResultId and we're not clearing
  if (executiveSummary && !isCompleted && !shouldClearSummary && executiveSummary.qa_result_id === selectedQAResultId) {
    const isStale = currentQAResult ? isStalePending(currentQAResult) : false;
    return (
      <div className="w-full h-full overflow-auto" style={{ background: 'var(--color-zone-recessed)' }}>
        <div className="p-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold eg-text-primary">Executive Summary</h1>
            <p className="eg-text-secondary mt-1">
              {executiveSummary.pdf_filename} | {executiveSummary.checklist_name} {executiveSummary.checklist_version}
            </p>
          </div>

          <div className="flex flex-col items-center justify-center py-12">
            {isStale ? (
              <div className="mb-6 p-6 bg-red-400/10 border border-red-400/30 rounded-lg w-full max-w-md">
                <div className="flex items-center gap-3 mb-4">
                  <XCircle className="w-6 h-6 text-red-400" />
                  <div className="flex-1">
                    <p className="text-lg font-semibold text-red-400">
                      QA Extraction Failed
                    </p>
                    <p className="text-sm eg-text-secondary mt-1">
                      This extraction has been pending for an unusually long time and likely failed. Please contact support for assistance.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-6 p-6 bg-yellow-400/10 border border-yellow-400/30 rounded-lg w-full max-w-md">
                <div className="flex items-center gap-3 mb-4">
                  <Loader2 className="w-6 h-6 animate-spin text-yellow-400" />
                  <div className="flex-1">
                    <p className="text-lg font-semibold text-yellow-400">
                      {status
                        ? `QA Extraction ${status === 'processing' ? 'in Progress' : 'Pending'}`
                        : 'Loading QA Extraction Status...'}
                    </p>
                    <p className="text-sm eg-text-secondary mt-1">
                      {status
                        ? `The QA extraction is currently ${status}. Results will be available once processing completes.`
                        : 'Fetching QA extraction status for this site...'}
                    </p>
                  </div>
                </div>

                {currentQAResult?.meta?.task_id && (
                  <div className="mt-4 pt-4 border-t border-yellow-400/20">
                    <p className="text-xs eg-text-secondary">Task ID: {currentQAResult.meta.task_id}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No QA result for this site (items [] / total 0) or no summary yet: show list or "No QA Results Available"
  // Don't show summary if we're clearing (site changed) or if summary doesn't match current result
  if (!selectedQAResultId || !executiveSummary || shouldClearSummary || executiveSummary.qa_result_id !== selectedQAResultId) {
    return (
      <div className="eg-page-shell">
        <div className="eg-page-header">
          <div className="eg-page-header-glow" />
          <div className="px-6 py-5 relative">
            <h1 className="eg-page-header-title">Executive Summary</h1>
            <p className="eg-page-header-subtitle">
              {currentSite?.site_name || 'Site'} - Select a QA result to view executive summary
            </p>
          </div>
          <div className="eg-page-accent-divider" />
        </div>
        <div className="eg-page-content">
          <div className="px-6 py-5">

          {qaResultsData && (qaResultsData.items?.length ?? 0) > 0 ? (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold eg-text-primary mb-4">
                QA Results for {currentSite?.site_name || 'this site'} ({qaResultsData.items.length} total)
              </h2>
              {qaResultsData.items.map((result: QAResultNew) => (
                <div
                  key={result.id}
                  className={`p-4 eg-card-elevated hover:brightness-105 transition-all cursor-pointer ${selectedQAResultId === result.id
                      ? 'ring-2 ring-engramiq-green/20'
                      : ''
                    }`}
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('openTab', {
                      detail: {
                        tabType: 'qaexecutivesummary',
                        params: { qaResultId: result.id }
                      }
                    }));
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="w-5 h-5 text-engramiq-green" />
                        <span className="text-sm font-mono eg-text-secondary">
                          {result.pdf_filename}
                        </span>
                        {isStalePending(result) ? (
                          <span
                            className="px-2 py-1 rounded text-xs bg-red-400/20 text-red-400 cursor-help"
                            title="This extraction has been pending too long and likely failed. Please contact support."
                          >
                            failed
                          </span>
                        ) : (
                          <span className={`px-2 py-1 rounded text-xs ${result.status === 'completed' ? 'bg-green-400/20 text-green-400' :
                              result.status === 'pending' ? 'bg-yellow-400/20 text-yellow-400' :
                                'bg-red-400/20 text-red-400'
                            }`}>
                            {result.status}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm eg-text-secondary mb-2">
                        <span>{result.checklist_name} {result.checklist_version}</span>
                      </div>
                    </div>
                    <div className="text-right text-xs eg-text-secondary">
                      <div>{new Date(result.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <BarChart3 className="w-16 h-16 eg-text-secondary mb-4" />
              <p className="text-lg eg-text-primary mb-2">No QA Results Available</p>
              <p className="text-sm eg-text-secondary mb-6">
                No QA results found for {currentSite?.site_name || 'this site'}
              </p>

              {/* Show pending/processing status if extraction is in progress */}
              {pendingOrProcessingResult && (
                <div className="mb-6 p-4 bg-yellow-400/10 border border-yellow-400/30 rounded-lg w-full max-w-md">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-yellow-400" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-yellow-400">
                        QA extraction in progress...
                      </p>
                      <p className="text-xs eg-text-secondary mt-1">
                        Status: {pendingOrProcessingResult.status}
                        {pendingOrProcessingResult.meta?.task_id && (
                          <> • Task ID: {pendingOrProcessingResult.meta.task_id}</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col items-center gap-4 w-full max-w-md">
                <button
                  onClick={async () => {
                    if (!siteId) {
                      alert('Site ID is required');
                      return;
                    }
                    try {
                      await triggerQAExtraction({
                        site_id: siteId,
                      }).unwrap();
                      alert('QA extraction task queued successfully!');
                      refetchResults();
                    } catch (error: any) {
                      const errorMessage = error?.data?.detail || error?.message || 'Unknown error';
                      if (error?.status === 409) {
                        alert(`QA extraction already in progress. ${errorMessage}`);
                      } else if (error?.status === 404) {
                        alert(errorMessage);
                      } else {
                        alert(`Failed to trigger QA extraction: ${errorMessage}`);
                      }
                    }
                  }}
                  disabled={isTriggering || !!pendingOrProcessingResult}
                  className="flex items-center gap-2 px-6 py-2 bg-engramiq-green text-engramiq-dark-blue font-semibold rounded-lg hover:bg-engramiq-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isTriggering ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : pendingOrProcessingResult ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Extraction in Progress</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      <span>Generate QA</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    );
  }

  // Only show results when status is completed
  // At this point, executiveSummary must exist and be completed (otherwise we would have returned earlier)
  if (!executiveSummary || !isCompleted) {
    return null; // Safety guard - should not happen
  }

  const documentName = executiveSummary.pdf_filename;
  const questionSetName = executiveSummary.checklist_name;
  const questionSetVersion = executiveSummary.checklist_version;

  return (
    <div className="eg-page-shell">
      {/* Header */}
      <div className="eg-page-header">
        <div className="eg-page-header-glow" />
        <div className="px-6 py-5 relative">
          <h1 className="eg-page-header-title">Executive Summary</h1>
          <p className="eg-page-header-subtitle">
            {documentName} | {questionSetName} {questionSetVersion}
          </p>
        </div>
        <div className="eg-page-accent-divider" />
      </div>
      <div className="eg-page-content">
        <div className="px-6 py-5">

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <MetricCard
            icon={<FileText className="text-blue-400" />}
            label="Total Pages"
            value={executiveSummary.total_pages}
          />
          <MetricCard
            icon={<HelpCircle className="text-purple-400" />}
            label="Questions Checked"
            value={executiveSummary.total_questions}
          />
          <MetricCard
            icon={<CheckCircle className="text-green-400" />}
            label="Pass Rate"
            value={`${Number(executiveSummary.pass_rate).toFixed(1)}%`}
          />
        </div>

        {/* Category Breakdown - Bar Chart */}
        {categoryChartData.length > 0 && chartsReady && (
          <div className="eg-card-elevated p-6 mb-8">
            <h2 className="text-lg font-semibold eg-text-primary mb-4">Results by Category</h2>
            <div
              className="h-72 w-full min-w-[400px] select-none [&_svg]:outline-none [&_svg_*]:outline-none"
              onClickCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
            >
              <ResponsiveContainer width="100%" height="100%" minWidth={400} minHeight={288}>
                <BarChart data={categoryChartData} margin={{ bottom: 60, left: 10, right: 10, top: 10 }}>
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: 'var(--color-text-secondary)', angle: -45, textAnchor: 'end' }}
                    interval={0}
                    height={70}
                  />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }} />
                  <Tooltip
                    cursor={{ fill: 'var(--color-surface-hover)', opacity: 0.5 }}
                    contentStyle={{
                      backgroundColor: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                      color: 'var(--color-text-primary)',
                    }}
                    itemStyle={{ color: 'var(--color-text-primary)' }}
                    labelStyle={{ color: 'var(--color-text-primary)' }}
                    formatter={(value: number, name: string) => [value, name.charAt(0).toUpperCase() + name.slice(1)]}
                    labelFormatter={(label: string) => {
                      const item = categoryChartData.find((d) => d.name === label);
                      return item?.fullName || label;
                    }}
                  />
                  <Bar dataKey="fail" stackId="a" fill={COLORS.fail} name="Fail" activeBar={false} style={{ cursor: 'default' }} />
                  <Bar dataKey="pass" stackId="a" fill={COLORS.pass} name="Pass" activeBar={false} style={{ cursor: 'default' }} />
                  <Bar dataKey="ca" stackId="a" fill={COLORS.ca} name="Can't Assess" activeBar={false} style={{ cursor: 'default' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Bottom row: Pie chart + Top Failures */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Pass/Fail Distribution */}
          <div className="eg-card-elevated p-6">
            <h2 className="text-lg font-semibold eg-text-primary mb-4">Overall Results</h2>
            <div className="flex items-center">
              {chartsReady && pieData.length > 0 && (
                <div className="w-40 h-40 min-w-[160px] min-h-[160px]">
                  <ResponsiveContainer width="100%" height="100%" minWidth={160} minHeight={160}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={65}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--color-surface)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px',
                          color: 'var(--color-text-primary)',
                        }}
                        itemStyle={{ color: 'var(--color-text-primary)' }}
                        labelStyle={{ color: 'var(--color-text-primary)' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="flex-1 space-y-2.5 ml-4">
                <LegendItem
                  color={COLORS.pass}
                  label="Pass"
                  count={executiveSummary.total_passed}
                  percent={Number(executiveSummary.pass_rate)}
                />
                <LegendItem
                  color={COLORS.fail}
                  label="Fail"
                  count={executiveSummary.total_failed}
                  percent={executiveSummary.total_answered > 0
                    ? (executiveSummary.total_failed / executiveSummary.total_answered) * 100
                    : 0}
                />
                <LegendItem
                  color={COLORS.ca}
                  label="Can't Assess"
                  count={executiveSummary.total_unanswerable}
                  percent={executiveSummary.total_answered > 0
                    ? (executiveSummary.total_unanswerable / executiveSummary.total_answered) * 100
                    : 0}
                />
              </div>
            </div>
          </div>

          {/* Top Failure Categories */}
          {topFailureCategories.length > 0 && (
            <div className="eg-card-elevated p-6">
              <h2 className="text-lg font-semibold eg-text-primary mb-4">Top Failure Categories</h2>
              <div className="space-y-2">
                {topFailureCategories.map((cat) => (
                  <div key={cat.category} className="flex items-center gap-4">
                    <XCircle size={16} className="text-red-400 flex-shrink-0" />
                    <span className="flex-1 eg-text-primary text-sm">{cat.category}</span>
                    <span className="text-red-400 font-medium text-sm flex-shrink-0">{cat.fail} failures</span>
                    <div className="w-24 bg-[var(--color-surface-hover)] rounded-full h-2 flex-shrink-0">
                      <div
                        className="bg-red-500 h-2 rounded-full"
                        style={{ width: `${(cat.fail / cat.total) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

function MetricCard({
  icon,
  label,
  value,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div
      className={`eg-card-elevated p-4 flex items-center gap-4 ${highlight ? 'ring-2 ring-orange-400/20' : ''
        }`}
    >
      <div className="p-3 bg-[var(--color-surface-hover)] rounded-lg">{icon}</div>
      <div>
        <p className="text-sm eg-text-secondary">{label}</p>
        <p className="text-2xl font-bold eg-text-primary">{value}</p>
      </div>
    </div>
  );
}

function LegendItem({
  color,
  label,
  count,
  percent,
}: {
  color: string;
  label: string;
  count: number;
  percent: number;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-4 h-4 rounded" style={{ backgroundColor: color }} />
      <span className="eg-text-secondary flex-1">{label}</span>
      <span className="font-medium eg-text-primary">{count}</span>
      <span className="eg-text-secondary text-sm">({percent.toFixed(1)}%)</span>
    </div>
  );
}

export default QAExecutiveSummaryTab;
